import { X } from '@phosphor-icons/react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { InputNumber } from 'antd';
import { HttpStatusCode } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AccentButton, NeutralButton } from '../../../../../../../shared/Buttons';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../../../utils/UserContext';
import {
  ApiError,
  ensureApiSuccess,
  unwrapApiResponse,
} from '../../../../../../../utils/unwrapApiResponse';
import { formatCurrency } from '../../../../../../../utils/CurrencyUtils';
import { BalancesResponse, LoanDetails } from '../../../../../../../types/apiTypes';
import PayTowardAdvanceSuccess from './PayTowardAdvanceSuccess';

// Matches the backend minimum (waived on a full payoff).
const MIN_PAYMENT_DOLLARS = 100;

// The payment settles asynchronously (the transfer runs in the workflow after the
// request returns). We refetch when the customer dismisses the success screen — the
// time spent reading it is roughly the settle window — plus once more shortly after,
// in case they dismiss it immediately.
const BALANCE_REFRESH_DELAY_MS = 5000;

type PayTowardAdvanceFormData = {
  amount: number;
};

/**
 * Lets a customer pay down an active cash advance from their TACH balance. Sends
 * either { amount_cents } or { pay_in_full: true } to the credit service, which
 * starts the payment workflow. The endpoint returns as soon as it's submitted, so
 * on success we tell the customer the balance will update shortly and refetch loans.
 *
 * The payment is capped client-side at the lesser of the remaining advance balance
 * and the customer's available TACH balance (the backend enforces this too).
 */
export const PayTowardAdvanceModal = ({
  onClose,
  onSubmitted,
  loan,
}: {
  onClose: () => void;
  onSubmitted?: () => void;
  loan: LoanDetails;
}) => {
  const { callProtectedApi } = useApi();
  const { userId } = useUser();
  const queryClient = useQueryClient();

  // current_balance is in dollars (same as the rest of the loan detail screen).
  const remainingBalance = Math.max(0, Number(loan?.current_balance ?? 0));

  // The customer's available TACH balance (same "Total balance" the withdrawal uses).
  const {
    data: balances,
    isLoading: balancesLoading,
    isError: balancesError,
  } = useQuery<BalancesResponse>({
    queryKey: ['balancesData', userId],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/internalBankAccounts/${userId ?? ''}/balances`,
        'get',
        {},
        'Could not load your TACH balance.'
      );
      const data = unwrapApiResponse<{ balances: BalancesResponse }>(
        response,
        'Could not load your TACH balance.'
      );
      return data.balances;
    },
    enabled: !!userId,
    staleTime: 60 * 1000,
  });

  const availableBalance = Math.max(0, Number(balances?.budgetedBalance ?? 0));
  const balanceReady = !balancesLoading && !balancesError && balances != null;

  // Can't pay more than either the balance owed or what's in the TACH account.
  const maxPayable = Math.min(remainingBalance, availableBalance);
  const canPayInFull = balanceReady && availableBalance >= remainingBalance;
  // Under the $100 minimum, the only option is to pay it off in full.
  const mustPayInFull =
    remainingBalance > 0 && remainingBalance < MIN_PAYMENT_DOLLARS;

  const [payInFullChecked, setPayInFullChecked] = useState(mustPayInFull);
  // Switches the modal to the success screen once the payment is submitted.
  const [succeeded, setSucceeded] = useState(false);
  // Effective mode: forced to full when under the minimum; forced to custom when a
  // full payoff isn't affordable; otherwise the customer's choice.
  const payInFull = mustPayInFull ? true : !canPayInFull ? false : payInFullChecked;

  const {
    control,
    watch,
    formState: { errors },
  } = useForm<PayTowardAdvanceFormData>({
    mode: 'onChange',
    defaultValues: { amount: 0 },
  });

  const amount = watch('amount');

  const invalidatePaymentQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['loans'] });
    queryClient.invalidateQueries({ queryKey: ['loansWithCharts'] });
    queryClient.invalidateQueries({ queryKey: ['balancesData'] });
  };

  const paymentMutation = useMutation({
    mutationKey: ['manualCashAdvancePayment', loan.loan_id],
    mutationFn: async () => {
      const body = payInFull
        ? { pay_in_full: true }
        : { amount_cents: Math.round(amount * 100) };
      const response = await callProtectedApi(
        `/credit/loans/${loan.loan_id}/manual-payment`,
        'post',
        body,
        'Something went wrong processing your payment.'
      );
      ensureApiSuccess(
        response,
        'Something went wrong processing your payment.'
      );
    },
    onSuccess: () => {
      setSucceeded(true);
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        if (error.status === HttpStatusCode.TooManyRequests) {
          toast.error(
            'A payment is already in progress. Please try again in a moment.'
          );
        } else {
          // The backend returns a user-friendly message on 400/409 (insufficient
          // funds, exceeds balance, below minimum, etc.) — surface it directly.
          toast.error(error.message);
        }
        return;
      }
      toast.error('Something went wrong processing your payment.');
    },
  });

  const isSubmitting = paymentMutation.isPending;
  const amountInvalid =
    !payInFull &&
    (!amount || amount < MIN_PAYMENT_DOLLARS || amount > maxPayable);
  // Nothing valid can be submitted at all (e.g. balance below the minimum and not
  // enough to clear the advance in full).
  const nothingPayable =
    balanceReady &&
    ((payInFull && !canPayInFull) ||
      (!payInFull && maxPayable < MIN_PAYMENT_DOLLARS));
  // Show the custom-amount field unless we're paying in full or the balance is too
  // low for any valid amount (keep it visible while the balance is still loading).
  const showCustomInput =
    !payInFull && (!balanceReady || maxPayable >= MIN_PAYMENT_DOLLARS);
  const submitDisabled =
    isSubmitting ||
    remainingBalance <= 0 ||
    !balanceReady ||
    nothingPayable ||
    amountInvalid;

  const handlePay = () => {
    if (submitDisabled) return;
    paymentMutation.mutate();
  };

  const handleDone = () => {
    onSubmitted?.();
    // The customer just spent a few seconds on the success screen, so the transfer
    // has likely settled — refetch now and once more shortly after (covers a fast
    // dismiss): the detail page + list (['loans', ...]), the banking-overview cards
    // (['loansWithCharts']), and the TACH balance (['balancesData']).
    invalidatePaymentQueries();
    setTimeout(invalidatePaymentQueries, BALANCE_REFRESH_DELAY_MS);
    onClose();
  };

  if (succeeded) {
    return <PayTowardAdvanceSuccess onClose={handleDone} />;
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="h-auto max-h-[90%] w-[95%] max-w-[520px] overflow-x-hidden rounded-[4px] bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
          <h2 className="text-[16px] font-[600] text-primary">
            Make a Payment Towards Your Advance
          </h2>
          <X
            className="hover:cursor-pointer hover:opacity-80"
            onClick={onClose}
            weight="regular"
            size={24}
          />
        </div>

        <form
          className="flex w-full flex-col gap-4 border-[1px] border-bordergray p-6"
          onSubmit={(e) => {
            e.preventDefault();
            handlePay();
          }}
        >
          <div className="flex w-full flex-row gap-3">
            <div className="flex w-full flex-col rounded-[4px] border-[1px] border-bordergray p-3">
              <p className="text-[14px] text-graysecondary">Remaining balance</p>
              <h2 className="text-[20px] font-[700] text-primary">
                {formatCurrency(remainingBalance)}
              </h2>
            </div>
            <div className="flex w-full flex-col rounded-[4px] border-[1px] border-bordergray p-3">
              <p className="text-[14px] text-graysecondary">Your TACH balance</p>
              {balancesLoading ? (
                <ClipLoader color="#1D213B" loading size={20} className="mt-1" />
              ) : balancesError ? (
                <p className="mt-1 text-[14px] text-red-500">Unable to load</p>
              ) : (
                <h2 className="text-[20px] font-[700] text-primary">
                  {formatCurrency(availableBalance)}
                </h2>
              )}
            </div>
          </div>

          {/* "Pay off in full" is only offered when the TACH balance can cover it. */}
          {canPayInFull && (
            <label className="flex flex-row items-center gap-2 text-[14px] text-primary">
              <input
                type="checkbox"
                checked={payInFull}
                disabled={mustPayInFull}
                onChange={(e) => setPayInFullChecked(e.target.checked)}
                className="h-4 w-4"
              />
              Pay off in full ({formatCurrency(remainingBalance)})
            </label>
          )}
          {canPayInFull && mustPayInFull && (
            <p className="text-[13px] text-graysecondary">
              This advance is under {formatCurrency(MIN_PAYMENT_DOLLARS)}, so it
              can only be paid off in full.
            </p>
          )}
          {nothingPayable && (
            <p className="text-[13px] text-[#E2660C]">
              Your TACH balance ({formatCurrency(availableBalance)}) isn&apos;t
              enough to make a payment on this advance right now.
            </p>
          )}

          {showCustomInput && (
            <div className="flex w-full flex-col">
              <label
                htmlFor="amount"
                className="block text-[14px] font-medium text-graysecondary"
              >
                Payment amount
              </label>
              <Controller
                name="amount"
                control={control}
                rules={{
                  required: 'Enter an amount',
                  min: {
                    value: MIN_PAYMENT_DOLLARS,
                    message: `Minimum payment is ${formatCurrency(MIN_PAYMENT_DOLLARS)}`,
                  },
                  max: {
                    value: maxPayable,
                    message:
                      availableBalance < remainingBalance
                        ? `Amount can't exceed your TACH balance (${formatCurrency(availableBalance)})`
                        : "Amount can't exceed your remaining balance",
                  },
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <InputNumber
                    id="amount"
                    min={0}
                    max={maxPayable}
                    step={0.01}
                    precision={2}
                    disabled={!balanceReady}
                    value={value}
                    onChange={(val) => onChange(Number(val ?? 0))}
                    onBlur={onBlur}
                    formatter={(val) => `$${val}`}
                    parser={(val) =>
                      parseFloat(val?.replace(/[$,]/g, '') || '') || 0
                    }
                    className="mt-1 w-full"
                  />
                )}
              />
              {errors.amount && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.amount.message}
                </p>
              )}
            </div>
          )}

          <p className="text-[13px] text-graysecondary">
            This payment comes out of your TACH balance and goes toward this cash
            advance&apos;s remaining balance.
          </p>

          <div className="flex flex-row justify-between gap-3 pt-2">
            <NeutralButton
              onClick={onClose}
              addClass="rounded-full w-full h-[48px]"
            >
              Cancel
            </NeutralButton>
            <AccentButton
              onClick={handlePay}
              disabled={submitDisabled}
              addClass="rounded-full w-full h-[48px] disabled:opacity-50"
            >
              {isSubmitting ? (
                <ClipLoader
                  color="#1D213B"
                  loading
                  size={20}
                  aria-label="Loading Spinner"
                />
              ) : (
                'Submit payment'
              )}
            </AccentButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayTowardAdvanceModal;
